/**
 * FontRow — pick a family from the ones this render can actually resolve.
 *
 * The branded `Select`, never a native one (operator rule, 2026-09-16: "never
 * put the native ones"). The OS popup paints in its own theme and cannot show
 * each family in its own face; the Select lists every row as a specimen and
 * keeps the keyboard and type-ahead a native control used to bring. The
 * inspector's `FontPicker` (search, recents, upload) stays the rich surface
 * one "More options" click away — this row has one job: swap the face.
 *
 * `sameAsLabel` is a CALLER-computed note ("Same as your caption font"): only
 * the caller knows whether two roles currently share a family, and saying so
 * is what stops someone hunting for a difference that isn't there.
 *
 * No preview/commit pair: picking a family is a discrete choice, so the host
 * commits it directly through `onPick` (the Select only fires on a change).
 */
import { fieldLabelStyle } from "../../styles.ts";
import { Select } from "../../ui/Select.tsx";

export function FontRow({ label, value, families, sameAsLabel, onPick }: {
  label: string;
  value: string;
  families: string[];
  sameAsLabel?: string;
  onPick: (family: string) => void;
}): React.JSX.Element {
  // A family the clip renders with but the picker list never heard of (a
  // legacy brand face) still has to show as the current value.
  const options = families.includes(value) ? families : [value, ...families];
  return (
    <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
      <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>{label}</span>
      <Select
        value={value}
        aria-label={label}
        onChange={onPick}
        size="sm"
        options={options.map((f) => ({ value: f, label: f, style: { fontFamily: `"${f}", var(--ql-font-sans)` } }))}
      />
      {sameAsLabel && (
        <span style={{ fontSize: 11, color: "var(--ql-fg-subtle)", lineHeight: 1.45 }}>{sameAsLabel}</span>
      )}
    </div>
  );
}
